import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAllCompaniesService } from "../../services/Company/CompanyServices";
import {
  searchAvailableSuppliersService,
  updateCompanySuppliers,
} from "../../services/Supplier/SupplierService";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface NamedRef {
  _id: string;
  name: string;
}

interface CompanyRow extends NamedRef {
  suppliers: NamedRef[];
}

/**
 * "Suppliers" is a real many-to-many relation between companies (Company.suppliers
 * in the backend, company_suppliers join table) — company A picks other existing
 * companies as its suppliers, it isn't free-text data of its own. This mirrors
 * exactly what the producer-facing flow already does correctly (ProfileSupplier.tsx):
 * load the companies eligible to be a supplier (SupplierController.getAvailableSuppliers),
 * let the admin pick from that list for the target company, and submit the chosen
 * companies back with their real `id` set (the backend deserializes the request body
 * straight into JPA Company entities, keyed by `id` — not the DTOs' `_id` alias).
 */
const AdminSuppliers = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [availableSuppliers, setAvailableSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [managingCompany, setManagingCompany] = useState<CompanyRow | null>(null);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
  const [savingList, setSavingList] = useState<boolean>(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([getAllCompaniesService(), searchAvailableSuppliersService()])
      .then(([companiesRes, availableRes]: any[]) => {
        setCompanies(companiesRes.data ?? []);
        setAvailableSuppliers(availableRes.data ?? []);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredCompanies = companies.filter((c) => {
    const search = globalFilterValue.trim().toLowerCase();
    if (!search) return true;
    return c.name?.toLowerCase().includes(search);
  });

  const openManageDialog = (company: CompanyRow) => {
    setManagingCompany(company);
    setSelectedSupplierIds((company.suppliers ?? []).map((s) => s._id));
  };

  const saveManagedList = () => {
    if (!managingCompany) return;
    setSavingList(true);
    const selectedCompanies = availableSuppliers
      .filter((c) => selectedSupplierIds.includes(c._id))
      .map((c) => ({ ...c, id: c._id }));
    updateCompanySuppliers(selectedCompanies, { input: managingCompany._id })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setManagingCompany(null);
        loadAll();
      })
      .catch(() => showError(toast, t("common:genericError")))
      .finally(() => setSavingList(false));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader title={t("common:suppliers")} />

      <div className="admin-page-body">
      <div className="p-input-icon-left mb-3">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={(e) => setGlobalFilterValue(e.target.value)}
          placeholder="Keyword Search"
        />
      </div>

      <DataTable
        value={filteredCompanies}
        loading={loading}
        paginator
        rows={20}
        dataKey="_id"
        sortField="name"
        sortOrder={1}
        emptyMessage={t("common:noResults")}
      >
        <Column field="name" header={t("common:Name")} sortable />
        <Column
          header={t("common:suppliers")}
          body={(row: CompanyRow) =>
            row.suppliers && row.suppliers.length > 0
              ? row.suppliers.map((s) => s.name).join(", ")
              : "-"
          }
        />
        <Column
          body={(row: CompanyRow) => (
            <Button
              className="general-button"
              onClick={() => openManageDialog(row)}
            >
              {t("common:modifyCompanies")}
            </Button>
          )}
        />
      </DataTable>
      </div>

      <Dialog
        header={managingCompany?.name}
        visible={managingCompany != null}
        onHide={() => setManagingCompany(null)}
        style={{ width: "40vw" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button
              className="general-button"
              onClick={() => setManagingCompany(null)}
              disabled={savingList}
            >
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={saveManagedList} disabled={savingList}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        <div className="field">
          <label>{t("common:suppliers")}</label>
          <MultiSelect
            style={{ width: "100%" }}
            value={selectedSupplierIds}
            options={availableSuppliers
              .filter((c) => c._id !== managingCompany?._id)
              .map((c) => ({ value: c._id, label: c.name }))}
            onChange={(e) => setSelectedSupplierIds(e.value as string[])}
            display="chip"
          />
        </div>
      </Dialog>
    </div>
  );
};

export default AdminSuppliers;
